import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Building2, FileText, FileWarning, Download, CheckCircle2, Clock, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

const getAlertBadge = (alert, daysToExpiry) => {
  if (alert === "expired") return <Badge className="status-missing">Expired</Badge>;
  if (alert === "30_days") return <Badge className="status-pending">Expires in {daysToExpiry} days</Badge>;
  if (alert === "90_days") return <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30">Expires in {daysToExpiry} days</Badge>;
  return <Badge className="status-complete">Active</Badge>;
};

export const LeaseCard = ({ lease, onTogglePayment, onUploadAgreement, onDownloadAgreement, toggling }) => {
  const [showTerms, setShowTerms] = useState(false);
  const isPaid = lease.payment_status === "paid";
  const isExpired = lease.alert === "expired";
  const canEdit = lease.can_edit;
  const terms = lease.agreement?.terms;

  return (
    <div
      className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors"
      data-testid={`lease-item-${lease.lease_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-white font-medium">{lease.tenant_name}</h3>
            <p className="text-slate-400 text-sm">{lease.tenant_email}</p>
            {lease.asset_name && (
              <p className="text-slate-500 text-sm flex items-center gap-1 mt-0.5" data-testid={`lease-asset-${lease.lease_id}`}>
                <Building2 className="w-3.5 h-3.5" /> {lease.asset_name}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {getAlertBadge(lease.alert, lease.days_to_expiry)}
          {!isExpired && (
            <Badge
              className={isPaid ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/30"}
              data-testid={`payment-status-badge-${lease.lease_id}`}
            >
              {isPaid ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
              {isPaid ? "Rent Paid" : "Rent Pending"}
            </Badge>
          )}
          {!isExpired && lease.days_to_next_period >= 0 && lease.days_to_next_period <= 3 && (
            <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/30" data-testid={`rent-due-badge-${lease.lease_id}`}>
              <AlertTriangle className="w-3 h-3 mr-1" />
              {lease.days_to_next_period === 0 ? "Next rent due today" : `Next rent due in ${lease.days_to_next_period}d`}
            </Badge>
          )}
          {lease.has_agreement ? (
            <Badge className="bg-slate-700/60 text-slate-300 border-slate-600" data-testid={`agreement-badge-${lease.lease_id}`}>
              <FileText className="w-3 h-3 mr-1" /> Agreement attached
            </Badge>
          ) : (
            <Badge className="bg-red-500/10 text-red-400 border-red-500/30" data-testid={`agreement-badge-${lease.lease_id}`}>
              <FileWarning className="w-3 h-3 mr-1" /> Agreement missing
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div>
          <p className="text-slate-500 text-xs">Rent Amount</p>
          <p className="text-white font-medium mono">{formatCurrency(lease.rent_amount)}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">Frequency</p>
          <p className="text-white font-medium capitalize">{lease.payment_frequency}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">Current Period From</p>
          <p className="text-white font-medium">{lease.current_period_start ? new Date(lease.current_period_start).toLocaleDateString() : "—"}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">Start Date</p>
          <p className="text-white font-medium">{new Date(lease.start_date).toLocaleDateString()}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">End Date</p>
          <p className="text-white font-medium">{new Date(lease.end_date).toLocaleDateString()}</p>
        </div>
      </div>

      {(canEdit || lease.has_agreement || terms) && (
        <div className="mt-4 pt-4 border-t border-slate-700 flex flex-wrap items-center gap-2">
          {canEdit && !isExpired && (
            <Button
              size="sm"
              disabled={toggling}
              onClick={() => onTogglePayment(lease, isPaid ? "pending" : "paid")}
              className={isPaid ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}
              data-testid={`toggle-payment-btn-${lease.lease_id}`}
            >
              {isPaid ? <Clock className="w-4 h-4 mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {isPaid ? "Mark as Pending" : "Mark as Paid"}
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className={lease.has_agreement ? "btn-secondary" : "border-red-500/40 text-red-300 hover:bg-red-500/10"}
              onClick={() => onUploadAgreement(lease)}
              data-testid={`upload-agreement-btn-${lease.lease_id}`}
            >
              <FileText className="w-4 h-4 mr-1" />
              {lease.has_agreement ? "Update Agreement" : "Upload Agreement & Terms"}
            </Button>
          )}
          {lease.has_agreement && (
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-300 hover:text-white"
              onClick={() => onDownloadAgreement(lease.agreement.file_id, lease.agreement.filename)}
              data-testid={`download-agreement-btn-${lease.lease_id}`}
            >
              <Download className="w-4 h-4 mr-1" /> {lease.agreement.filename}
            </Button>
          )}
          {terms && (
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-white ml-auto"
              onClick={() => setShowTerms((v) => !v)}
              data-testid={`toggle-terms-btn-${lease.lease_id}`}
            >
              {showTerms ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              Terms
            </Button>
          )}
        </div>
      )}

      {showTerms && terms && (
        <div className="mt-3 p-3 rounded-lg bg-slate-900/60 border border-slate-700 text-sm text-slate-300 whitespace-pre-wrap" data-testid={`lease-terms-${lease.lease_id}`}>
          {terms}
        </div>
      )}
    </div>
  );
};
